import { Box, Container, ContainerProps, Typography } from "@mui/material";
import React, { useContext } from "react";
import clsx from "clsx";
import makeStyles from "@mui/styles/makeStyles";
import { EnterProgramInviteCode } from "./EnterProgramInviteCode";
import { ProgramInvitePreview } from "./ProgramInvitePreview";
import { ProgramInvitePlaceholder } from "./ProgramInvitePlaceholder";
import { ValidatedInviteCodeDto } from "@growth-machine-llc/stridist-api-client";
import { useQuery } from "@tanstack/react-query";
import { CLIENT_ENROLLMENTS_STATUS_COUNT_QUERY_KEY } from "../../routes/client/programs/list/ClientProgramListRoute";
import EnrollmentsClientService from "../../services/EnrollmentsClientService";
import ProgramsContext from "../../contexts/ClientProgramsContext";

export interface ClientProfileScreenProps
  extends Omit<ContainerProps, "children"> {}

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    maxWidth: theme.breakpoints.values.slg,
  },

  layout: {
    display: "flex",
    gap: theme.spacing(4),
    alignItems: "center",
    justifyContent: "space-around",
    flexWrap: "wrap",
  },

  leftColumn: {
    flex: "1 1 420px",
    maxWidth: 420,
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
    },
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(3),
  },

  rightColumn: {
    flex: "1 1 380px",
    maxWidth: 500,
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
    },
  },

  header: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
}));

const ClientProgramsInviteCode = (props: ClientProfileScreenProps) => {
  const s = useStyles();
  const { className, ...other } = props;

  const [programInvite, setProgramInvite] =
    React.useState<ValidatedInviteCodeDto | null>(null);

  const context = useContext(ProgramsContext);

  const { data: enrollmentsCountData } = useQuery({
    queryKey: [CLIENT_ENROLLMENTS_STATUS_COUNT_QUERY_KEY],
    queryFn: () => EnrollmentsClientService.getClientEnrolmentsCount(),
  });

  React.useEffect(() => {
    enrollmentsCountData && context.setEnrollmentsCount(enrollmentsCountData);
  }, [enrollmentsCountData]);

  return (
    <Container className={clsx(s.root, className)} {...other}>
      <Box className={s.layout}>
        <Box className={s.leftColumn}>
          <Box className={s.header}>
            <Typography variant="h5">Program invite code</Typography>
            <Typography variant="body2" color="text.secondary">
              Enter the invite code you received to join a program.
            </Typography>
          </Box>

          <EnterProgramInviteCode onSuccess={setProgramInvite} />
        </Box>

        <Box className={s.rightColumn}>
          {programInvite ? (
            <ProgramInvitePreview invite={programInvite} />
          ) : (
            <ProgramInvitePlaceholder />
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default ClientProgramsInviteCode;
